import { z } from "zod";
import { OrderTypeSchema, colomboClock, formatDailyNumber, type OnlineChannel } from "../core";
import { priceOrder, promotionActiveAt, type ManualDiscount } from "../pricing";
import { Tx, type Reader } from "../store";
import { appOrderReady, portionRecipe, promotionFitsOrder, restockMilli } from "../types";
import type {
  Coupon, Customer, DiscountInput, InventoryItem, KitchenTicket, MenuItem, Order, OrderLine, PaymentMethod, Promotion, Settings
} from "../types";
import { ApprovalSchema, audit, can, fail, getSettings, lkr, newId, parse, raiseAlert, requirePermission, verifyApprover, type Approval, type Ctx } from "./context";

const PhoneSchema = z.string().regex(/^\+?[0-9]{9,15}$/, "Enter a valid phone number");
export const OrderLineInputSchema = z.object({
  menuItemId: z.string().min(1).max(128), sizeId: z.string().max(128).optional(),
  modifierOptionIds: z.array(z.string().min(1).max(128)).max(30).default([]),
  quantity: z.number().int().min(1).max(999), note: z.string().max(200).default(""), complimentary: z.boolean().default(false)
});
const DiscountInputSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("promotion"), promotionId: z.string().min(1).max(128) }),
  z.object({ kind: z.literal("coupon"), code: z.string().min(2).max(40) }),
  z.object({ kind: z.literal("manual"), mode: z.enum(["percent", "fixed"]), value: z.number().int().positive(), reason: z.string().min(2).max(200), lineId: z.string().max(128).optional() }),
  z.object({ kind: z.literal("staff") }),
  z.object({ kind: z.literal("loyalty"), points: z.number().int().positive() })
]);
const ComplimentarySchema = z.object({
  kind: z.string().min(2).max(60), reason: z.string().min(3).max(300), fullOrder: z.boolean().default(false),
  // Nothing is given away anonymously: the receiver is named, and the approver is recorded by the engine.
  recipient: z.string().trim().min(2, "Enter the name of the person receiving it").max(80)
});
const PaymentInputSchema = z.object({
  method: z.enum(["cash", "card", "bank_transfer"]), amountMinor: z.number().int().positive(),
  tenderedMinor: z.number().int().positive().optional(), reference: z.string().max(80).default("")
});
export const CreateOrderInputSchema = z.object({
  idempotencyKey: z.string().uuid(), type: OrderTypeSchema, customerPhone: PhoneSchema.optional(), waiterName: z.string().trim().max(60).optional(),
  customerName: z.string().max(100).default(""), tableId: z.string().min(1).max(128).optional(),
  lines: z.array(OrderLineInputSchema).min(1).max(100), discounts: z.array(DiscountInputSchema).max(10).default([]),
  complimentary: ComplimentarySchema.optional(), note: z.string().max(300).default(""),
  approval: ApprovalSchema.optional(), payments: z.array(PaymentInputSchema).max(6).default([])
}).superRefine((value, context) => {
  if (value.type === "call" && !value.customerPhone) context.addIssue({ code: "custom", path: ["customerPhone"], message: "Phone is required for call orders" });
  if (value.type === "dine_in" && !value.tableId) context.addIssue({ code: "custom", path: ["tableId"], message: "Table is required for dine-in orders" });
});
export type CreateOrderInput = z.input<typeof CreateOrderInputSchema>;

const OPEN_FOR_CHANGES = ["unpaid", "partial"];

/**
 * Every dine-in bill names the waiter looking after the table. With a waiter list (Tables → Waiters) the
 * name must be one of it; without one, any name typed at the counter is taken.
 */
function dineInWaiter(settings: Settings, name: string | undefined): string {
  const waiter = (name ?? "").trim();
  if (!waiter) fail("invalid-argument", "Choose the waiter serving this table.");
  const listed = settings.waiterNames ?? [];
  if (listed.length > 0 && !listed.includes(waiter)) fail("invalid-argument", `${waiter} is not on the waiter list. Choose a waiter from the list.`);
  return waiter;
}

async function loadOrder(ctx: Ctx, orderId: string): Promise<Order> {
  return await ctx.tx.get("orders", orderId) ?? fail("not-found", "Order not found.");
}
/** An app order that still waits has not reached the kitchen: it is changed only through accepting or rejecting it. */
function requireAccepted(order: Order): void {
  if (order.onlineState === "pending") fail("failed-precondition", `App order ${order.dailyNumber} is still waiting. Accept or reject it first.`);
}

function itemAvailable(item: MenuItem, now: Date): boolean {
  if (!item.availability) return true;
  const { minutes, weekday } = colomboClock(now);
  const { days, fromMinutes: from, toMinutes: to } = item.availability;
  if (days.length > 0 && !days.includes(weekday)) return false;
  if (from === to) return true;
  return from < to ? minutes >= from && minutes < to : minutes >= from || minutes < to;
}

async function buildLines(ctx: Ctx, inputs: z.infer<typeof OrderLineInputSchema>[], batch: number, forceComplimentary: boolean): Promise<{ lines: OrderLine[]; items: Map<string, MenuItem> }> {
  const items = new Map<string, MenuItem>();
  const lines: OrderLine[] = [];
  for (const input of inputs) {
    const item = items.get(input.menuItemId) ?? await ctx.tx.get("menuItems", input.menuItemId) ?? fail("not-found", "A menu item no longer exists.");
    items.set(item.id, item);
    if (!item.active) fail("failed-precondition", `${item.name} is not available.`);
    // Marked as run out for today (Menu screen): neither the counter nor the app can sell it until it is put back.
    if (item.soldOutDate === ctx.businessDate) fail("failed-precondition", `${item.name} is sold out today. Mark it available on the Menu screen when it is back.`);
    if (!itemAvailable(item, ctx.now)) fail("failed-precondition", `${item.name} is not served at this time.`);
    const size = input.sizeId ? item.sizes.find((entry) => entry.id === input.sizeId) ?? fail("invalid-argument", `Unknown size for ${item.name}.`) : undefined;
    if (!size && item.sizes.length > 0) fail("invalid-argument", `Choose a size for ${item.name}.`);
    const modifiers: OrderLine["modifiers"] = [];
    const optionIds: string[] = [];
    const chosen = new Set(input.modifierOptionIds);
    for (const group of item.modifierGroups) {
      const picked = group.options.filter((option) => chosen.has(option.id));
      if (picked.length < group.min || picked.length > group.max) fail("invalid-argument", `${item.name}: choose ${group.min === group.max ? group.min : `${group.min}–${group.max}`} option(s) for ${group.name}.`);
      for (const option of picked) { modifiers.push({ name: option.name, priceMinor: option.priceMinor }); optionIds.push(option.id); chosen.delete(option.id); }
    }
    if (chosen.size > 0) fail("invalid-argument", `Unknown option selected for ${item.name}.`);
    let unitCostMinor = size?.costMinor ?? item.costMinor;
    const recipe = portionRecipe(item, size?.id, optionIds);
    if (recipe.length > 0) {
      let cost = 0;
      for (const part of recipe) cost += Math.round(((await ctx.tx.get("inventoryItems", part.inventoryItemId))?.avgCostMinor ?? 0) * part.qtyMilli / 1000);
      unitCostMinor = cost;
    }
    lines.push({
      id: newId(), menuItemId: item.id, name: item.name, sizeName: size?.name ?? null, sizeId: size?.id ?? null, modifierOptionIds: optionIds, modifiers,
      unitPriceMinor: (size?.priceMinor ?? item.priceMinor) + modifiers.reduce((sum, modifier) => sum + modifier.priceMinor, 0),
      unitCostMinor, quantity: input.quantity, note: input.note, complimentary: forceComplimentary || input.complimentary,
      discountMinor: 0, kotBatch: batch, voided: false, categoryId: item.categoryId
    });
  }
  return { lines, items };
}

/**
 * The stock the given lines use, per ingredient. A line saved before portion recipes has no size id and finds its size
 * by name; one whose size has since been removed falls back to the dish's own recipe.
 */
async function stockNeeds(ctx: Ctx, lines: readonly OrderLine[]): Promise<Map<string, { sale: number; complimentary: number }>> {
  const totals = new Map<string, { sale: number; complimentary: number }>();
  for (const line of lines) {
    const item = await ctx.tx.get("menuItems", line.menuItemId);
    if (!item) continue;
    const sizeId = line.sizeId ?? item.sizes.find((size) => size.name === line.sizeName)?.id;
    const optionIds = line.modifierOptionIds ?? item.modifierGroups.flatMap((group) => group.options).filter((option) => line.modifiers.some((modifier) => modifier.name === option.name)).map((option) => option.id);
    for (const part of portionRecipe(item, sizeId, optionIds)) {
      const entry = totals.get(part.inventoryItemId) ?? { sale: 0, complimentary: 0 };
      entry[line.complimentary ? "complimentary" : "sale"] += part.qtyMilli * line.quantity;
      totals.set(part.inventoryItemId, entry);
    }
  }
  return totals;
}

const stockQty = (milli: number, unit: string) => `${Number((milli / 1000).toFixed(3))} ${unit}`;

/**
 * Refuses lines whose ingredients the stock cannot cover, so a dish that has run out is not sold. Switched off with
 * `stockBlocksSale: false` in the settings, for a kitchen that has not entered its stock yet.
 */
async function requireStock(ctx: Ctx, lines: readonly OrderLine[], settings: Settings): Promise<void> {
  if (settings.stockBlocksSale === false) return;
  for (const [inventoryItemId, quantities] of await stockNeeds(ctx, lines)) {
    const stock = await ctx.tx.get("inventoryItems", inventoryItemId);
    if (!stock || !stock.active) continue;
    const need = quantities.sale + quantities.complimentary;
    if (need <= stock.stockMilli) continue;
    fail("failed-precondition", stock.stockMilli <= 0
      ? `${stock.name} is out of stock, so this cannot be billed. Record the purchase under Inventory first.`
      : `Only ${stockQty(stock.stockMilli, stock.unit)} of ${stock.name} is left; this order needs ${stockQty(need, stock.unit)}. Reduce the quantity or record a purchase under Inventory.`);
  }
}

/** Deducts (or returns, when `direction` is +1) recipe ingredients for the given lines, one movement per ingredient. */
async function moveStock(ctx: Ctx, order: Order, lines: OrderLine[], direction: -1 | 1): Promise<void> {
  if (direction === -1) await requireStock(ctx, lines, await getSettings(ctx.tx));
  for (const [inventoryItemId, quantities] of await stockNeeds(ctx, lines)) {
    const stock = await ctx.tx.get("inventoryItems", inventoryItemId);
    if (!stock) continue;
    let balance = stock.stockMilli;
    for (const kind of ["sale", "complimentary"] as const) {
      if (quantities[kind] === 0) continue;
      balance += direction * quantities[kind];
      ctx.tx.set("stockMovements", {
        id: newId(), inventoryItemId, itemName: stock.name, kind: direction === 1 ? "void_return" : kind,
        deltaMilli: direction * quantities[kind], balanceAfterMilli: balance, unitCostMinor: stock.avgCostMinor,
        refType: "order", refId: order.id, reason: direction === 1 ? `Returned from ${order.dailyNumber}` : order.dailyNumber,
        createdBy: ctx.actor.id, createdByName: ctx.actor.name, createdAt: ctx.iso, businessDate: ctx.businessDate, schemaVersion: 1
      });
    }
    ctx.tx.set("inventoryItems", { ...stock, stockMilli: balance, updatedAt: ctx.iso });
    warnIfLow(ctx, stock, balance);
  }
}

/**
 * Tells management (Management View and their phones) the moment an item drops to its reorder level, and again when it
 * runs out: once for each.
 */
function warnIfLow(ctx: Ctx, stock: InventoryItem, balanceMilli: number): void {
  if (!stock.active || stock.reorderMilli <= 0) return;
  const reachedReorder = stock.stockMilli > stock.reorderMilli && balanceMilli <= stock.reorderMilli;
  const ranOut = stock.stockMilli > 0 && balanceMilli <= 0;
  if (!reachedReorder && !ranOut) return;
  raiseAlert(ctx, "low_stock", balanceMilli <= 0 ? `Out of stock: ${stock.name}` : `Low stock: ${stock.name}`,
    `${stockQty(Math.max(0, balanceMilli), stock.unit)} left (reorder level ${stockQty(stock.reorderMilli, stock.unit)}). Buy about ${stockQty(restockMilli({ ...stock, stockMilli: balanceMilli }), stock.unit)}.`);
}

/**
 * Records what was sent to the kitchen. With the kitchen display switched off the printed KOT is
 * the whole workflow, so the ticket is stored as already handled and never waits for a status tap.
 */
function createTicket(ctx: Ctx, order: Order, lines: OrderLine[], batch: number, settings: Settings, note = ""): KitchenTicket {
  const ticket: KitchenTicket = {
    id: newId(), orderId: order.id, dailyNumber: order.dailyNumber, type: order.type, tableName: order.tableName,
    customerPhone: order.customerPhone, batch, incremental: batch > 1, note: note || order.note,
    lines: lines.map((line) => ({ name: line.name, sizeName: line.sizeName, modifiers: line.modifiers.map((modifier) => modifier.name), quantity: line.quantity, note: line.note, complimentary: line.complimentary })),
    status: settings.kitchenDisplayEnabled ? "queued" : "served", ...(settings.kitchenDisplayEnabled ? {} : { servedAt: ctx.iso }),
    createdAt: ctx.iso, createdByName: order.waiterName || ctx.actor.name, businessDate: ctx.businessDate, schemaVersion: 1
  };
  ctx.tx.set("kitchenTickets", ticket);
  order.kitchenState = settings.kitchenDisplayEnabled ? "pending" : "served";
  return ticket;
}

async function adjustLoyalty(ctx: Ctx, customerId: string, delta: number, kind: "earn" | "redeem" | "reverse" | "restore" | "adjust", orderId: string | null, reason: string, extra: Partial<Customer> = {}): Promise<void> {
  const customer = await ctx.tx.get("customers", customerId);
  if (!customer) return;
  const points = Math.max(0, customer.points + delta);
  ctx.tx.set("customers", { ...customer, ...extra, points, updatedAt: ctx.iso });
  if (delta !== 0) ctx.tx.set("loyaltyTransactions", {
    id: newId(), customerId, orderId, kind, points: delta, balanceAfter: points, reason,
    createdBy: ctx.actor.id, createdAt: ctx.iso, businessDate: ctx.businessDate, schemaVersion: 1
  });
}

async function releaseCoupon(ctx: Ctx, order: Order, couponId: string): Promise<void> {
  const redemption = (await ctx.tx.query("couponRedemptions", [{ field: "orderId", op: "==", value: order.id }])).find((entry) => entry.couponId === couponId && !entry.reversed);
  if (!redemption) return;
  ctx.tx.set("couponRedemptions", { ...redemption, reversed: true });
  const coupon = await ctx.tx.get("coupons", couponId);
  if (coupon) ctx.tx.set("coupons", { ...coupon, redeemedCount: Math.max(0, coupon.redeemedCount - 1), updatedAt: ctx.iso });
}

/**
 * Re-resolves every discount against trusted data and recalculates the order. Coupon redemptions
 * and loyalty points are reconciled against what the order previously held, so repeating the
 * operation never double-counts. `authorize` enforces discount thresholds for newly chosen discounts.
 */
async function reprice(ctx: Ctx, order: Order, settings: Settings, authorize: boolean, approval: Approval | undefined): Promise<void> {
  const previousCouponId = order.discounts.find((discount) => discount.kind === "coupon")?.couponId;
  const previousPoints = order.loyaltyPointsRedeemed;
  const available = await ctx.tx.query("promotions", [{ field: "active", op: "==", value: true }]);
  const promotions: Promotion[] = available.filter((promotion) => promotion.autoApply && promotionActiveAt(promotion, ctx.now) && promotionFitsOrder(promotion, order));
  const manual: ManualDiscount[] = [];
  let coupon: Coupon | undefined;
  let loyaltyPoints = 0;
  let needsManagerSpecial = false;
  const kept: DiscountInput[] = [];
  for (const input of order.discountInputs) {
    if (input.kind === "manual") { manual.push(input); kept.push(input); continue; }
    if (input.kind === "loyalty") { loyaltyPoints = input.points; kept.push(input); continue; }
    if (input.kind === "coupon") {
      const code = input.code.trim().toUpperCase();
      const found = (await ctx.tx.query("coupons", [{ field: "code", op: "==", value: code }]))[0];
      if (!found) fail("not-found", `Coupon ${code} does not exist.`);
      if (found.id !== previousCouponId) {
        if (!found.active) fail("failed-precondition", `Coupon ${code} is not active.`);
        if ((found.startDate && ctx.businessDate < found.startDate) || (found.endDate && ctx.businessDate > found.endDate)) fail("failed-precondition", `Coupon ${code} is not valid today.`);
        if (found.maxRedemptions > 0 && found.redeemedCount >= found.maxRedemptions) fail("failed-precondition", `Coupon ${code} has reached its redemption limit.`);
        if (found.perCustomerLimit > 0) {
          if (!order.customerId) fail("failed-precondition", `Coupon ${code} needs a customer phone number.`);
          const used = (await ctx.tx.query("couponRedemptions", [{ field: "couponId", op: "==", value: found.id }])).filter((entry) => entry.customerId === order.customerId && !entry.reversed).length;
          if (used >= found.perCustomerLimit) fail("failed-precondition", `This customer has already used coupon ${code}.`);
        }
      }
      if (coupon) fail("invalid-argument", "Only one coupon can be applied to an order.");
      coupon = found; kept.push({ kind: "coupon", code });
      continue;
    }
    const promotion = input.kind === "staff"
      ? available.find((entry) => entry.kind === "staff" && promotionActiveAt(entry, ctx.now))
      : available.find((entry) => entry.id === input.promotionId && promotionActiveAt(entry, ctx.now));
    if (!promotion) { if (authorize) fail("failed-precondition", "The selected promotion is not active right now."); continue; }
    if (promotion.kind === "staff" || promotion.kind === "manager_special") needsManagerSpecial = true;
    if (!promotions.some((entry) => entry.id === promotion.id)) promotions.push(promotion);
    kept.push(input);
  }
  order.discountInputs = kept;

  const customer = order.customerId ? await ctx.tx.get("customers", order.customerId) : undefined;
  if (loyaltyPoints > 0) {
    if (!settings.loyaltyEnabled) fail("failed-precondition", "Loyalty redemption is switched off.");
    if (!customer) fail("failed-precondition", "Loyalty points need a customer on the order.");
    loyaltyPoints = Math.min(loyaltyPoints, customer.points + previousPoints);
  }
  const priced = priceOrder({
    type: order.type, lines: order.lines, taxBps: order.taxBps, serviceChargeBps: order.serviceChargeBps, promotions, coupon, manual,
    loyalty: loyaltyPoints > 0 ? { points: loyaltyPoints, pointValueMinor: settings.loyaltyPointValueMinor } : undefined
  });
  if (authorize && priced.rejected.length > 0) fail("failed-precondition", priced.rejected[0]!);

  if (authorize) {
    if (order.discountInputs.length > 0) requirePermission(ctx, "discounts.apply");
    const manualTotal = priced.discounts.filter((discount) => discount.kind === "manual").reduce((sum, discount) => sum + discount.amountMinor, 0);
    const payableGross = priced.totals.grossMinor - priced.totals.complimentaryMinor;
    const bps = payableGross > 0 ? Math.ceil((manualTotal * 10_000) / payableGross) : 0;
    order.discountApprovedBy = null;
    if (bps > 0 || needsManagerSpecial) {
      const approver = await verifyApprover(ctx, approval, needsManagerSpecial ? "complimentary.approve" : "discounts.apply", (user) => settings.thresholds[user.role].maxDiscountBps >= bps);
      order.discountApprovedBy = approver.name;
      audit(ctx, "discount.authorised", "order", order.id, { dailyNumber: order.dailyNumber, manualMinor: manualTotal, bps, special: needsManagerSpecial }, { approvedBy: approver.id });
      if (settings.alertDiscountBps > 0 && bps >= settings.alertDiscountBps && bps > (order.alertedDiscountBps ?? 0) && !ctx.dryRun) {
        order.alertedDiscountBps = bps;
        raiseAlert(ctx, "discount", `Discount ${Math.round(bps / 100)}% on ${order.dailyNumber}`, `${lkr(manualTotal)} off a ${lkr(payableGross)} bill. Given by ${ctx.actor.name}, approved by ${approver.name}.`, order.id);
      }
    }
  }

  const appliedCoupon = priced.discounts.find((discount) => discount.kind === "coupon");
  if (previousCouponId && previousCouponId !== appliedCoupon?.couponId) await releaseCoupon(ctx, order, previousCouponId);
  if (appliedCoupon?.couponId && coupon) {
    if (appliedCoupon.couponId !== previousCouponId) {
      ctx.tx.set("coupons", { ...coupon, redeemedCount: coupon.redeemedCount + 1, updatedAt: ctx.iso });
      ctx.tx.set("couponRedemptions", { id: newId(), couponId: coupon.id, code: coupon.code, orderId: order.id, customerId: order.customerId, amountMinor: appliedCoupon.amountMinor, reversed: false, createdAt: ctx.iso, businessDate: ctx.businessDate, schemaVersion: 1 });
    } else {
      const redemption = (await ctx.tx.query("couponRedemptions", [{ field: "orderId", op: "==", value: order.id }])).find((entry) => entry.couponId === coupon.id && !entry.reversed);
      if (redemption) ctx.tx.set("couponRedemptions", { ...redemption, amountMinor: appliedCoupon.amountMinor });
    }
  }
  const usedPoints = priced.discounts.find((discount) => discount.kind === "loyalty")?.points ?? 0;
  if (order.customerId && usedPoints !== previousPoints) {
    await adjustLoyalty(ctx, order.customerId, previousPoints - usedPoints, usedPoints > previousPoints ? "redeem" : "restore", order.id, `Order ${order.dailyNumber}`);
  }
  order.loyaltyPointsRedeemed = usedPoints;
  order.lines = priced.lines; order.discounts = priced.discounts; order.totals = priced.totals;
  if (order.totals.totalMinor < order.paidMinor) fail("failed-precondition", "The bill cannot drop below the amount already paid. Refund first.");
  order.paymentState = paymentStateOf(order);
}

function paymentStateOf(order: Order): Order["paymentState"] {
  if (order.refundedMinor > 0) return order.refundedMinor >= order.paidMinor ? "refunded" : "partially_refunded";
  if (order.totals.totalMinor === 0) return order.totals.complimentaryMinor > 0 ? "complimentary" : "paid";
  if (order.paidMinor >= order.totals.totalMinor) return "paid";
  return order.paidMinor > 0 ? "partial" : "unpaid";
}

async function freeTable(ctx: Ctx, order: Order): Promise<void> {
  if (!order.tableId) return;
  const table = await ctx.tx.get("tables", order.tableId);
  if (table && table.activeOrderId === order.id) ctx.tx.set("tables", { ...table, status: "available", activeOrderId: null, waiterId: null, waiterName: null });
}

/** Runs once, when an order becomes fully settled: loyalty earning, customer statistics and table release. */
async function settle(ctx: Ctx, order: Order, settings: Settings): Promise<void> {
  if (order.customerId) {
    const earned = settings.loyaltyEnabled && settings.loyaltySpendPerPointMinor > 0 ? Math.floor(order.totals.totalMinor / settings.loyaltySpendPerPointMinor) : 0;
    const customer = await ctx.tx.get("customers", order.customerId);
    if (customer) {
      await adjustLoyalty(ctx, customer.id, earned, "earn", order.id, `Order ${order.dailyNumber}`, {
        totalSpentMinor: customer.totalSpentMinor + order.totals.totalMinor, orderCount: customer.orderCount + 1, lastOrderAt: ctx.iso
      });
      order.loyaltyPointsEarned = earned;
    }
  }
  if (order.type === "dine_in") {
    order.status = "completed"; order.fulfillmentState = "served"; order.completedAt = ctx.iso;
    await freeTable(ctx, order);
  }
}

async function openShiftId(ctx: Ctx): Promise<string> {
  const shift = (await ctx.tx.query("shifts", [{ field: "status", op: "==", value: "open" }]))[0];
  return shift?.id ?? fail("failed-precondition", "Open a cash shift before taking payments.");
}

async function applyPayments(ctx: Ctx, order: Order, payments: z.infer<typeof PaymentInputSchema>[], settings: Settings): Promise<string[]> {
  requirePermission(ctx, "payments.capture");
  if (order.status !== "open") fail("failed-precondition", "This order is already closed.");
  const due = order.totals.totalMinor - order.paidMinor;
  const amount = payments.reduce((sum, payment) => sum + payment.amountMinor, 0);
  if (due === 0 && amount > 0) fail("failed-precondition", "Nothing is due on this order.");
  if (amount > due) fail("invalid-argument", "Payment exceeds the amount due.");
  const ids: string[] = [];
  if (payments.length > 0) {
    const shiftId = await openShiftId(ctx);
    for (const payment of payments) {
      const tendered = payment.method === "cash" ? payment.tenderedMinor ?? payment.amountMinor : payment.amountMinor;
      if (tendered < payment.amountMinor) fail("invalid-argument", "Cash tendered is less than the amount.");
      const id = newId();
      ids.push(id);
      ctx.tx.set("payments", {
        id, orderId: order.id, dailyNumber: order.dailyNumber, method: payment.method, amountMinor: payment.amountMinor,
        tenderedMinor: tendered, changeMinor: tendered - payment.amountMinor, reference: payment.reference, shiftId,
        createdBy: ctx.actor.id, createdByName: ctx.actor.name, createdAt: ctx.iso, businessDate: ctx.businessDate, schemaVersion: 1
      });
    }
  }
  const wasSettled = order.paidMinor >= order.totals.totalMinor && order.paidMinor > 0;
  order.paidMinor += amount;
  order.paymentState = paymentStateOf(order);
  if (!wasSettled && order.paidMinor >= order.totals.totalMinor) await settle(ctx, order, settings);
  audit(ctx, "payment.captured", "order", order.id, { dailyNumber: order.dailyNumber, amountMinor: amount, methods: payments.map((payment) => payment.method), state: order.paymentState });
  return ids;
}

function save(ctx: Ctx, order: Order): void {
  order.updatedAt = ctx.iso; order.version += 1;
  ctx.tx.set("orders", order);
}

function recordComplimentary(ctx: Ctx, order: Order, lines: OrderLine[], details: z.infer<typeof ComplimentarySchema>, approver: { id: string; name: string }): void {
  ctx.tx.set("complimentaryApprovals", {
    id: newId(), orderId: order.id, dailyNumber: order.dailyNumber, recipient: details.recipient, kind: details.kind, reason: details.reason, fullOrder: details.fullOrder,
    retailValueMinor: lines.reduce((sum, line) => sum + line.unitPriceMinor * line.quantity, 0),
    costMinor: lines.reduce((sum, line) => sum + line.unitCostMinor * line.quantity, 0),
    items: lines.map((line) => ({ name: line.name, quantity: line.quantity, valueMinor: line.unitPriceMinor * line.quantity })),
    requestedBy: ctx.actor.id, requestedByName: ctx.actor.name, approvedBy: approver.id, approvedByName: approver.name,
    createdAt: ctx.iso, businessDate: ctx.businessDate, schemaVersion: 1
  });
  audit(ctx, "complimentary.approved", "order", order.id, { dailyNumber: order.dailyNumber, kind: details.kind, items: lines.length }, { reason: details.reason, approvedBy: approver.id });
  raiseAlert(ctx, "complimentary", `Complimentary ${lkr(lines.reduce((sum, line) => sum + line.unitPriceMinor * line.quantity, 0))} on ${order.dailyNumber}`,
    `For ${details.recipient || "a guest"}: ${lines.map((line) => `${line.quantity} x ${line.name}`).join(", ").slice(0, 140)}. Approved by ${approver.name}.`, order.id);
}

const randomToken = (bytes: number) => Array.from(crypto.getRandomValues(new Uint8Array(bytes)), (byte) => byte.toString(16).padStart(2, "0")).join("");
/** Unambiguous characters only, so a reference can be read aloud or typed from a receipt. */
function randomRef(): string {
  const alphabet = "23456789ABCDEFGHJKLMNPQRSTUVWXYZ";
  return Array.from(crypto.getRandomValues(new Uint8Array(8)), (byte) => alphabet[byte % alphabet.length]).join("");
}

async function findOrCreateCustomer(ctx: Ctx, phone: string, name: string): Promise<Customer> {
  const existing = (await ctx.tx.query("customers", [{ field: "phone", op: "==", value: phone }]))[0];
  if (existing) return existing;
  const customer: Customer = { id: newId(), phone, name, birthday: "", notes: "", points: 0, totalSpentMinor: 0, orderCount: 0, lastOrderAt: null, marketingConsent: false, active: true, createdAt: ctx.iso, updatedAt: ctx.iso, schemaVersion: 1 };
  ctx.tx.set("customers", customer);
  return customer;
}

/** The daily counter of one online channel; kept apart from the counter's takeaway series. */
export const onlineCounterId = (businessDate: string, channel: OnlineChannel) => `${businessDate}_online_${channel}`;

export async function createOrder(ctx: Ctx, raw: unknown) {
  const result = await placeOrder(ctx, raw, null);
  return { ...result, ticketId: result.ticketId! };
}

/**
 * Creates the order. `online` marks an order a customer placed themselves (in the app or on the web): nothing goes
 * to the kitchen and no stock is used yet, it waits until staff accept it (see `online.accept`), and it takes its
 * number from its own daily series (A-001 for the app, W-001 for the web) instead of the counter's T-001.
 */
export async function placeOrder(ctx: Ctx, raw: unknown, online: OnlineChannel | null) {
  const hold = online !== null;
  requirePermission(ctx, "orders.create");
  const input = parse(CreateOrderInputSchema, raw);
  const settings = await getSettings(ctx.tx);
  const fullComplimentary = input.complimentary?.fullOrder ?? false;
  const { lines } = await buildLines(ctx, input.lines, 1, fullComplimentary);
  const complimentaryLines = lines.filter((line) => line.complimentary);
  if (complimentaryLines.length > 0 && !input.complimentary) fail("invalid-argument", "A reason is required for complimentary items.");

  let table = input.type === "dine_in" ? await ctx.tx.get("tables", input.tableId!) ?? fail("not-found", "Table not found.") : undefined;
  if (table && (!table.active || table.activeOrderId)) fail("failed-precondition", table.activeOrderId ? `${table.name} already has an open bill. Add items to that bill instead.` : `${table.name} is not in service.`);

  const waiterName = table ? dineInWaiter(settings, input.waiterName) : null;

  const customer = input.customerPhone ? await findOrCreateCustomer(ctx, input.customerPhone, input.customerName) : undefined;

  const counterId = online ? onlineCounterId(ctx.businessDate, online) : `${ctx.businessDate}_${input.type}`;
  const sequence = ((await ctx.tx.get("orderCounters", counterId))?.sequence ?? 0) + 1;
  ctx.tx.set("orderCounters", { id: counterId, businessDate: ctx.businessDate, type: online ? `online_${online}` : input.type, sequence });

  const order: Order = {
    id: newId(), ref: `MM${ctx.businessDate.slice(2).replaceAll("-", "")}${randomRef()}`, publicToken: randomToken(20),
    type: input.type, dailyNumber: formatDailyNumber(input.type, sequence, online ?? undefined), sequence, businessDate: ctx.businessDate,
    customerId: customer?.id ?? null, customerName: input.customerName || customer?.name || "", customerPhone: input.customerPhone ?? "",
    tableId: table?.id ?? null, tableName: table?.name ?? null, waiterId: null, waiterName, lines, discountInputs: input.discounts, discounts: [],
    totals: { grossMinor: 0, complimentaryMinor: 0, lineDiscountMinor: 0, billDiscountMinor: 0, discountMinor: 0, subtotalMinor: 0, serviceChargeMinor: 0, taxMinor: 0, totalMinor: 0, costMinor: 0 },
    taxBps: settings.taxBps, serviceChargeBps: settings.serviceChargeDineInOnly && input.type !== "dine_in" ? 0 : settings.serviceChargeBps,
    // A held order is one from the customer app: marked so before pricing, so app-only promotions apply to it.
    ...(online ? { source: "app" as const, channel: online, onlineState: "pending" as const } : {}),
    status: "open", paymentState: "unpaid", kitchenState: "pending", fulfillmentState: "open", paidMinor: 0, refundedMinor: 0,
    complimentary: null, discountApprovedBy: null, note: input.note, loyaltyPointsEarned: 0, loyaltyPointsRedeemed: 0, kotBatches: 1,
    createdBy: ctx.actor.id, createdByName: ctx.actor.name, createdAt: ctx.iso, updatedAt: ctx.iso, version: 0, schemaVersion: 1
  };
  if (complimentaryLines.length > 0 && input.complimentary) {
    const approver = await verifyApprover(ctx, input.approval, "complimentary.approve");
    order.complimentary = { kind: input.complimentary.kind, reason: input.complimentary.reason, recipient: input.complimentary.recipient, fullOrder: fullComplimentary, approvedBy: approver.id, approvedByName: approver.name };
    recordComplimentary(ctx, order, complimentaryLines, input.complimentary, approver);
  }
  await reprice(ctx, order, settings, true, input.approval);

  const ticket = hold ? undefined : createTicket(ctx, order, order.lines, 1, settings);
  // A held app order uses no stock yet, but is not taken for a dish that has already run out.
  if (hold) await requireStock(ctx, order.lines, settings);
  else await moveStock(ctx, order, order.lines, -1);
  if (table) ctx.tx.set("tables", table = { ...table, status: "occupied", activeOrderId: order.id, reservedFor: null, waiterId: null, waiterName });
  audit(ctx, "order.created", "order", order.id, { dailyNumber: order.dailyNumber, type: order.type, totalMinor: order.totals.totalMinor, items: order.lines.length });

  let paymentIds: string[] = [];
  if (input.payments.length > 0) paymentIds = await applyPayments(ctx, order, input.payments, settings);
  else if (order.totals.totalMinor === 0 && order.type !== "dine_in") await settle(ctx, order, settings);
  save(ctx, order);
  return { orderId: order.id, dailyNumber: order.dailyNumber, ref: order.ref, ticketId: ticket?.id ?? null, paymentIds };
}

/**
 * Prices a prospective order with every trusted rule (menu prices, promotions, coupons, loyalty)
 * without committing anything, so the cashier sees the exact amount before taking payment.
 */
export async function quoteOrder(ctx: Ctx, raw: unknown, fromApp = false) {
  // Any online channel prices the same way; the dry run only borrows its numbering.
  const scratch = new Tx(ctx.tx as unknown as Reader);
  const { orderId } = await placeOrder({ ...ctx, tx: scratch, dryRun: true, audits: [] }, { ...(raw as object), idempotencyKey: newId(), payments: [] }, fromApp ? "app" : null);
  const order = (await scratch.get("orders", orderId))!;
  return { totals: order.totals, discounts: order.discounts, serviceChargeBps: order.serviceChargeBps, taxBps: order.taxBps };
}

export async function addItems(ctx: Ctx, raw: unknown) {
  requirePermission(ctx, "orders.create");
  const input = parse(z.object({
    idempotencyKey: z.string().uuid(), orderId: z.string().min(1), lines: z.array(OrderLineInputSchema).min(1).max(100),
    complimentary: ComplimentarySchema.optional(), approval: ApprovalSchema.optional()
  }), raw);
  const order = await loadOrder(ctx, input.orderId);
  requireAccepted(order);
  if (order.status !== "open" || !OPEN_FOR_CHANGES.includes(order.paymentState)) fail("failed-precondition", "Items can only be added to an open, unsettled bill.");
  const batch = order.kotBatches + 1;
  const { lines } = await buildLines(ctx, input.lines, batch, false);
  const complimentaryLines = lines.filter((line) => line.complimentary);
  if (complimentaryLines.length > 0) {
    if (!input.complimentary) fail("invalid-argument", "A reason is required for complimentary items.");
    const approver = await verifyApprover(ctx, input.approval, "complimentary.approve");
    order.complimentary ??= { kind: input.complimentary.kind, reason: input.complimentary.reason, recipient: input.complimentary.recipient, fullOrder: false, approvedBy: approver.id, approvedByName: approver.name };
    recordComplimentary(ctx, order, complimentaryLines, { ...input.complimentary, fullOrder: false }, approver);
  }
  order.lines = [...order.lines, ...lines]; order.kotBatches = batch;
  const settings = await getSettings(ctx.tx);
  await reprice(ctx, order, settings, false, undefined);
  const added = order.lines.filter((line) => line.kotBatch === batch);
  const ticket = createTicket(ctx, order, added, batch, settings);
  await moveStock(ctx, order, added, -1);
  audit(ctx, "order.items_added", "order", order.id, { dailyNumber: order.dailyNumber, batch, items: added.length });
  save(ctx, order);
  return { orderId: order.id, ticketId: ticket.id, batch };
}

export async function setDiscounts(ctx: Ctx, raw: unknown) {
  const input = parse(z.object({ orderId: z.string().min(1), discounts: z.array(DiscountInputSchema).max(10), approval: ApprovalSchema.optional() }), raw);
  requirePermission(ctx, "discounts.apply");
  const order = await loadOrder(ctx, input.orderId);
  if (order.status !== "open" || order.paidMinor > 0) fail("failed-precondition", "Discounts can only change before any payment is taken.");
  order.discountInputs = input.discounts;
  await reprice(ctx, order, await getSettings(ctx.tx), true, input.approval);
  audit(ctx, "order.discounts_changed", "order", order.id, { dailyNumber: order.dailyNumber, discountMinor: order.totals.discountMinor, kinds: order.discounts.map((discount) => discount.kind) });
  save(ctx, order);
  return { totals: order.totals, discounts: order.discounts };
}

export async function payOrder(ctx: Ctx, raw: unknown) {
  const input = parse(z.object({ idempotencyKey: z.string().uuid(), orderId: z.string().min(1), payments: z.array(PaymentInputSchema).max(6) }), raw);
  const order = await loadOrder(ctx, input.orderId);
  const settings = await getSettings(ctx.tx);
  // An app order is paid at the counter when it is handed over, which is once it is ready.
  if (order.source === "app" && input.payments.length > 0 && !appOrderReady(order, ctx.now, settings.kitchenDisplayEnabled)) {
    fail("failed-precondition", `App order ${order.dailyNumber} is not ready yet. Press "Ready" on the order when it is, then take the payment.`);
  }
  if (input.payments.length === 0) {
    requirePermission(ctx, "payments.capture");
    // Only a zero-value dine-in bill (fully complimentary or fully discounted) is closed without a payment.
    if (order.status !== "open" || order.type !== "dine_in" || order.totals.totalMinor !== 0) fail("failed-precondition", "A payment is required to close this bill.");
    await settle(ctx, order, settings);
    audit(ctx, "order.closed", "order", order.id, { dailyNumber: order.dailyNumber });
  }
  const paymentIds = input.payments.length > 0 ? await applyPayments(ctx, order, input.payments, settings) : [];
  save(ctx, order);
  return { orderId: order.id, paymentState: order.paymentState, status: order.status, dueMinor: order.totals.totalMinor - order.paidMinor, paymentIds };
}

export async function collectOrder(ctx: Ctx, raw: unknown) {
  requirePermission(ctx, "orders.collect");
  const input = parse(z.object({ orderId: z.string().min(1) }), raw);
  const order = await loadOrder(ctx, input.orderId);
  if (order.type === "dine_in") fail("failed-precondition", "Dine-in bills are closed by settlement, not collection.");
  if (order.fulfillmentState === "collected") fail("failed-precondition", `Order ${order.dailyNumber} was already collected.`);
  requireAccepted(order);
  if (order.status !== "open") fail("failed-precondition", "This order is not open.");
  if (order.paymentState !== "paid" && order.paymentState !== "complimentary") fail("failed-precondition", "Take payment before handing over the order.");
  order.fulfillmentState = "collected"; order.status = "completed"; order.collectedAt = ctx.iso; order.collectedBy = ctx.actor.name; order.completedAt = ctx.iso;
  order.kitchenState = "served";
  for (const ticket of await ctx.tx.query("kitchenTickets", [{ field: "orderId", op: "==", value: order.id }])) {
    if (ticket.status !== "cancelled" && ticket.status !== "served") ctx.tx.set("kitchenTickets", { ...ticket, status: "served", servedAt: ctx.iso });
  }
  audit(ctx, "order.collected", "order", order.id, { dailyNumber: order.dailyNumber });
  save(ctx, order);
  return { orderId: order.id, dailyNumber: order.dailyNumber };
}

async function releaseOrderBenefits(ctx: Ctx, order: Order): Promise<void> {
  const couponId = order.discounts.find((discount) => discount.kind === "coupon")?.couponId;
  if (couponId) await releaseCoupon(ctx, order, couponId);
  if (order.customerId && order.loyaltyPointsRedeemed > 0) {
    await adjustLoyalty(ctx, order.customerId, order.loyaltyPointsRedeemed, "restore", order.id, `Order ${order.dailyNumber} cancelled`);
    order.loyaltyPointsRedeemed = 0;
  }
}

export async function voidOrder(ctx: Ctx, raw: unknown) {
  const input = parse(z.object({ orderId: z.string().min(1), reason: z.string().min(3).max(300), restock: z.boolean().default(false), approval: ApprovalSchema.optional() }), raw);
  const approver = await verifyApprover(ctx, input.approval, "orders.void");
  const order = await loadOrder(ctx, input.orderId);
  if (order.status !== "open") fail("failed-precondition", "Only open orders can be cancelled.");
  if (order.paidMinor - order.refundedMinor > 0) fail("failed-precondition", "Refund the payments before cancelling this order.");
  await releaseOrderBenefits(ctx, order);
  if (input.restock) await moveStock(ctx, order, order.lines.filter((line) => !line.voided), 1);
  for (const ticket of await ctx.tx.query("kitchenTickets", [{ field: "orderId", op: "==", value: order.id }])) {
    if (ticket.status !== "served") ctx.tx.set("kitchenTickets", { ...ticket, status: "cancelled" });
  }
  await freeTable(ctx, order);
  order.status = "voided"; order.voidedAt = ctx.iso; order.voidReason = input.reason;
  audit(ctx, "order.voided", "order", order.id, { dailyNumber: order.dailyNumber, totalMinor: order.totals.totalMinor, restock: input.restock }, { reason: input.reason, approvedBy: approver.id });
  raiseAlert(ctx, "void", `Order ${order.dailyNumber} cancelled (${lkr(order.totals.totalMinor)})`, `By ${ctx.actor.name}, approved by ${approver.name}. Reason: ${input.reason}`, order.id);
  save(ctx, order);
  return { orderId: order.id };
}

export async function voidLine(ctx: Ctx, raw: unknown) {
  const input = parse(z.object({ orderId: z.string().min(1), lineId: z.string().min(1), reason: z.string().min(3).max(300), restock: z.boolean().default(false), approval: ApprovalSchema.optional() }), raw);
  const approver = await verifyApprover(ctx, input.approval, "orders.void");
  const order = await loadOrder(ctx, input.orderId);
  requireAccepted(order);
  if (order.status !== "open" || !OPEN_FOR_CHANGES.includes(order.paymentState)) fail("failed-precondition", "Items can only be removed from an open, unsettled bill.");
  const line = order.lines.find((entry) => entry.id === input.lineId && !entry.voided) ?? fail("not-found", "Item not found on this bill.");
  if (order.lines.filter((entry) => !entry.voided).length === 1) fail("failed-precondition", "This is the last item. Cancel the whole order instead.");
  line.voided = true; line.voidReason = input.reason;
  if (input.restock) await moveStock(ctx, order, [line], 1);
  const settings = await getSettings(ctx.tx);
  await reprice(ctx, order, settings, false, undefined);
  order.kotBatches += 1;
  const kitchenState = order.kitchenState;
  const ticket = createTicket(ctx, order, [line], order.kotBatches, settings, `CANCEL ITEM — ${input.reason}`);
  // A cancellation notice must not push an order that was already being prepared back to "new".
  if (settings.kitchenDisplayEnabled && kitchenState !== "pending") order.kitchenState = kitchenState;
  audit(ctx, "order.line_voided", "order", order.id, { dailyNumber: order.dailyNumber, item: line.name, quantity: line.quantity }, { reason: input.reason, approvedBy: approver.id });
  raiseAlert(ctx, "void", `Item removed from ${order.dailyNumber}`, `${line.quantity} x ${line.name} (${lkr(line.unitPriceMinor * line.quantity)}). By ${ctx.actor.name}, approved by ${approver.name}. Reason: ${input.reason}`, order.id);
  save(ctx, order);
  return { orderId: order.id, ticketId: ticket.id };
}

export async function refundOrder(ctx: Ctx, raw: unknown) {
  const input = parse(z.object({
    idempotencyKey: z.string().uuid(), orderId: z.string().min(1), amountMinor: z.number().int().positive(),
    method: z.enum(["cash", "card", "bank_transfer"]), reason: z.string().min(3).max(300), approval: ApprovalSchema.optional()
  }), raw);
  const settings = await getSettings(ctx.tx);
  const approver = await verifyApprover(ctx, input.approval, "payments.refund", (user) => settings.thresholds[user.role].maxRefundMinor >= input.amountMinor);
  const order = await loadOrder(ctx, input.orderId);
  const refundable = order.paidMinor - order.refundedMinor;
  if (order.status === "voided" || refundable <= 0) fail("failed-precondition", "There is nothing to refund on this order.");
  if (input.amountMinor > refundable) fail("invalid-argument", "Refund exceeds the amount paid.");
  const shiftId = await openShiftId(ctx);
  const id = newId();
  ctx.tx.set("refunds", {
    id, orderId: order.id, dailyNumber: order.dailyNumber, method: input.method as PaymentMethod, amountMinor: input.amountMinor, reason: input.reason,
    shiftId, approvedBy: approver.id, approvedByName: approver.name, createdBy: ctx.actor.id, createdByName: ctx.actor.name,
    createdAt: ctx.iso, businessDate: ctx.businessDate, schemaVersion: 1
  });
  order.refundedMinor += input.amountMinor;
  const full = order.refundedMinor >= order.paidMinor;
  if (order.customerId) {
    const shouldBeReversed = full ? order.loyaltyPointsEarned : Math.floor((order.loyaltyPointsEarned * order.refundedMinor) / order.paidMinor);
    const alreadyReversed = (await ctx.tx.query("loyaltyTransactions", [{ field: "orderId", op: "==", value: order.id }])).filter((entry) => entry.kind === "reverse").reduce((sum, entry) => sum - entry.points, 0);
    const customer = await ctx.tx.get("customers", order.customerId);
    await adjustLoyalty(ctx, order.customerId, -(shouldBeReversed - alreadyReversed), "reverse", order.id, `Refund on ${order.dailyNumber}`, customer ? { totalSpentMinor: Math.max(0, customer.totalSpentMinor - input.amountMinor) } : {});
  }
  order.paymentState = paymentStateOf(order);
  if (full && order.status === "open") {
    await releaseOrderBenefits(ctx, order);
    await freeTable(ctx, order);
    order.status = "voided"; order.voidedAt = ctx.iso; order.voidReason = `Refunded: ${input.reason}`;
    for (const ticket of await ctx.tx.query("kitchenTickets", [{ field: "orderId", op: "==", value: order.id }])) {
      if (ticket.status !== "served") ctx.tx.set("kitchenTickets", { ...ticket, status: "cancelled" });
    }
  }
  audit(ctx, "payment.refunded", "order", order.id, { dailyNumber: order.dailyNumber, amountMinor: input.amountMinor, method: input.method, full }, { reason: input.reason, approvedBy: approver.id });
  raiseAlert(ctx, "refund", `Refund ${lkr(input.amountMinor)} on ${order.dailyNumber}`, `${input.method === "cash" ? "Cash" : "Card"} refund by ${ctx.actor.name}, approved by ${approver.name}. Reason: ${input.reason}`, order.id);
  save(ctx, order);
  return { refundId: id, paymentState: order.paymentState };
}

export async function transferTable(ctx: Ctx, raw: unknown) {
  requirePermission(ctx, "orders.create");
  const input = parse(z.object({ orderId: z.string().min(1), tableId: z.string().min(1) }), raw);
  const order = await loadOrder(ctx, input.orderId);
  if (order.type !== "dine_in" || order.status !== "open") fail("failed-precondition", "Only open dine-in bills can be moved.");
  const target = await ctx.tx.get("tables", input.tableId) ?? fail("not-found", "Table not found.");
  if (!target.active || target.activeOrderId) fail("failed-precondition", `${target.name} is not free.`);
  const from = order.tableName;
  const source = order.tableId ? await ctx.tx.get("tables", order.tableId) : undefined;
  if (source) ctx.tx.set("tables", { ...source, status: "available", activeOrderId: null, waiterId: null, waiterName: null });
  ctx.tx.set("tables", { ...target, status: "occupied", activeOrderId: order.id, reservedFor: null, waiterId: null, waiterName: order.waiterName ?? source?.waiterName ?? ctx.actor.name });
  order.tableId = target.id; order.tableName = target.name;
  for (const ticket of await ctx.tx.query("kitchenTickets", [{ field: "orderId", op: "==", value: order.id }])) ctx.tx.set("kitchenTickets", { ...ticket, tableName: target.name });
  audit(ctx, "order.table_transferred", "order", order.id, { dailyNumber: order.dailyNumber, from, to: target.name });
  save(ctx, order);
  return { orderId: order.id };
}

/**
 * Corrects an order entered under the wrong type. The order takes the next number of its new type
 * (the old number is left as an audited gap), charges are recalculated for the new type and the
 * kitchen receives a notice. Only possible before any payment, so no receipt is ever contradicted.
 */
export async function changeOrderType(ctx: Ctx, raw: unknown) {
  requirePermission(ctx, "orders.create");
  const input = parse(z.object({
    idempotencyKey: z.string().uuid(), orderId: z.string().min(1), type: OrderTypeSchema, tableId: z.string().min(1).max(128).optional(),
    waiterName: z.string().trim().max(60).optional(), customerPhone: PhoneSchema.optional(), customerName: z.string().max(100).default("")
  }), raw);
  const order = await loadOrder(ctx, input.orderId);
  requireAccepted(order);
  if (order.status !== "open" || order.paidMinor > 0) fail("failed-precondition", "The order type can only be changed on an open order before any payment.");
  if (order.type === input.type) fail("invalid-argument", "The order already has this type.");
  const settings = await getSettings(ctx.tx);
  const previous = order.dailyNumber;

  if (input.type === "dine_in") {
    if (!input.tableId) fail("invalid-argument", "Choose a table for a dine-in order.");
    const table = await ctx.tx.get("tables", input.tableId) ?? fail("not-found", "Table not found.");
    if (!table.active || table.activeOrderId) fail("failed-precondition", table.activeOrderId ? `${table.name} already has an open bill.` : `${table.name} is not in service.`);
    order.tableId = table.id; order.tableName = table.name; order.waiterName = dineInWaiter(settings, input.waiterName);
    ctx.tx.set("tables", { ...table, status: "occupied", activeOrderId: order.id, reservedFor: null, waiterId: null, waiterName: order.waiterName });
  } else {
    await freeTable(ctx, order);
    order.tableId = null; order.tableName = null; order.waiterName = null;
  }
  if (input.type === "call") {
    if (!input.customerPhone) fail("invalid-argument", "Phone is required for call orders");
    const customer = await findOrCreateCustomer(ctx, input.customerPhone, input.customerName);
    order.customerId = customer.id; order.customerPhone = customer.phone; order.customerName = input.customerName || customer.name;
  }

  const counterId = `${ctx.businessDate}_${input.type}`;
  const sequence = ((await ctx.tx.get("orderCounters", counterId))?.sequence ?? 0) + 1;
  ctx.tx.set("orderCounters", { id: counterId, businessDate: ctx.businessDate, type: input.type, sequence });
  order.type = input.type; order.sequence = sequence; order.dailyNumber = formatDailyNumber(input.type, sequence);
  order.serviceChargeBps = settings.serviceChargeDineInOnly && input.type !== "dine_in" ? 0 : settings.serviceChargeBps;
  await reprice(ctx, order, settings, false, undefined);

  for (const ticket of await ctx.tx.query("kitchenTickets", [{ field: "orderId", op: "==", value: order.id }])) {
    ctx.tx.set("kitchenTickets", { ...ticket, dailyNumber: order.dailyNumber, type: order.type, tableName: order.tableName, customerPhone: order.customerPhone });
  }
  const kitchenState = order.kitchenState;
  order.kotBatches += 1;
  const ticket = createTicket(ctx, order, order.lines.filter((line) => !line.voided), order.kotBatches, settings, `ORDER CHANGED — was ${previous}`);
  if (settings.kitchenDisplayEnabled && kitchenState !== "pending") order.kitchenState = kitchenState;
  audit(ctx, "order.type_changed", "order", order.id, { from: previous, to: order.dailyNumber, table: order.tableName ?? "", totalMinor: order.totals.totalMinor });
  save(ctx, order);
  return { orderId: order.id, dailyNumber: order.dailyNumber, ticketId: ticket.id };
}

const KITCHEN_ORDER = ["queued", "preparing", "ready", "served"] as const;
export async function setTicketStatus(ctx: Ctx, raw: unknown) {
  requirePermission(ctx, "kitchen.update");
  const input = parse(z.object({ ticketId: z.string().min(1), status: z.enum(["preparing", "ready", "served"]) }), raw);
  const ticket = await ctx.tx.get("kitchenTickets", input.ticketId) ?? fail("not-found", "Ticket not found.");
  if (ticket.status === "cancelled") fail("failed-precondition", "This ticket was cancelled.");
  if (KITCHEN_ORDER.indexOf(input.status) <= KITCHEN_ORDER.indexOf(ticket.status)) return { ticketId: ticket.id, status: ticket.status };
  const updated: KitchenTicket = {
    ...ticket, status: input.status, startedAt: ticket.startedAt ?? ctx.iso,
    ...(input.status !== "preparing" ? { readyAt: ticket.readyAt ?? ctx.iso } : {}), ...(input.status === "served" ? { servedAt: ctx.iso } : {})
  };
  ctx.tx.set("kitchenTickets", updated);
  const order = await ctx.tx.get("orders", ticket.orderId);
  if (order) {
    const tickets = (await ctx.tx.query("kitchenTickets", [{ field: "orderId", op: "==", value: order.id }])).filter((entry) => entry.status !== "cancelled");
    const lowest = Math.min(...tickets.map((entry) => KITCHEN_ORDER.indexOf(entry.status as (typeof KITCHEN_ORDER)[number])));
    const state = (["pending", "preparing", "ready", "served"] as const)[lowest] ?? "pending";
    if (state !== order.kitchenState) ctx.tx.set("orders", { ...order, kitchenState: state, updatedAt: ctx.iso, version: order.version + 1 });
  }
  return { ticketId: ticket.id, status: input.status };
}

export async function recordPrint(ctx: Ctx, raw: unknown) {
  const input = parse(z.object({
    id: z.string().uuid(), kind: z.enum(["kot", "receipt"]), orderId: z.string().min(1), ticketId: z.string().nullable().default(null),
    printerName: z.string().max(128), state: z.enum(["queued", "printed", "failed", "unknown"]), message: z.string().max(300).default(""),
    reprint: z.boolean().default(false), approval: ApprovalSchema.optional()
  }), raw);
  const existing = await ctx.tx.get("printJobs", input.id);
  if (existing) {
    ctx.tx.set("printJobs", { ...existing, state: input.state, message: input.message, printerName: input.printerName || existing.printerName, attempts: existing.attempts + (input.state === "queued" ? 0 : 1), updatedAt: ctx.iso });
    return { id: existing.id };
  }
  if (!can(ctx.actor, "orders.create") && !can(ctx.actor, "kitchen.read")) fail("permission-denied", "You cannot print for this order.");
  const order = await loadOrder(ctx, input.orderId);
  let approvedBy = "";
  if (input.reprint) {
    approvedBy = (await verifyApprover(ctx, input.approval, "receipts.reprint")).id;
    audit(ctx, "print.reprint", "order", order.id, { dailyNumber: order.dailyNumber, kind: input.kind }, { approvedBy });
  }
  ctx.tx.set("printJobs", {
    id: input.id, kind: input.kind, orderId: order.id, dailyNumber: order.dailyNumber, ticketId: input.ticketId, printerName: input.printerName,
    state: input.state, attempts: input.state === "queued" ? 0 : 1, message: input.message, reprint: input.reprint,
    createdBy: ctx.actor.id, createdByName: ctx.actor.name, createdAt: ctx.iso, updatedAt: ctx.iso, businessDate: ctx.businessDate, schemaVersion: 1
  });
  return { id: input.id };
}

export { adjustLoyalty, createTicket, itemAvailable, moveStock, paymentStateOf, reprice, settle, warnIfLow };
